/**
 * LayerManager - Manages layer visibility, frozen state, and queries.
 *
 * Wraps the stateless utility functions from utils/layers.ts and provides
 * a stateful API with automatic view updates.
 */

import type { Layer } from "@/types";
import type { DRDrawingViewer } from "../DRDrawingViewer";
import {
  getAllLayers,
  setLayerVisibility,
  setLayerFrozen,
  setLayerLocked,
  type LayerInfo,
} from "../database/layers";
import { ensureLayerExists, updateLayer } from "../layers/utils";

export class LayerManager {
  constructor(private viewer: DRDrawingViewer) {}

  /**
   * Get all layers in the drawing.
   * Excludes internal layers (prefixed with __DR_ or DR_).
   */
  getAll(): LayerInfo[] {
    if (!this.viewer.database) {
      return [];
    }
    return getAllLayers(
      this.viewer.module,
      this.viewer.database,
      this.viewer.getEffectiveBackgroundColor(),
    );
  }

  /**
   * Set layer visibility.
   * @param layerName Layer name
   * @param visible true = visible, false = hidden
   */
  setVisibility(layerName: string, visible: boolean): boolean {
    if (!this.viewer.database) {
      return false;
    }
    const result = setLayerVisibility(
      this.viewer.module,
      this.viewer.database,
      layerName,
      visible,
    );
    if (result) {
      this.viewer.update();
    }
    return result;
  }

  /**
   * Toggle layer visibility.
   * @param layerName Layer name
   * @returns New visibility state, or false if layer not found
   */
  toggleVisibility(layerName: string): boolean {
    const layers = this.getAll();
    const layer = layers.find((l) => l.name === layerName);
    if (!layer) return false;
    const newVisibility = !layer.isVisible;
    this.setVisibility(layerName, newVisibility);
    return newVisibility;
  }

  /**
   * Set layer frozen state.
   * @param layerName Layer name
   * @param frozen true = frozen, false = thawed
   */
  setFrozen(layerName: string, frozen: boolean): boolean {
    if (!this.viewer.database) {
      return false;
    }
    const result = setLayerFrozen(
      this.viewer.module,
      this.viewer.database,
      layerName,
      frozen,
    );
    if (result) {
      this.viewer.update();
    }
    return result;
  }

  /**
   * Toggle layer frozen state.
   * @param layerName Layer name
   * @returns New frozen state, or false if layer not found
   */
  toggleFrozen(layerName: string): boolean {
    const layers = this.getAll();
    const layer = layers.find((l) => l.name === layerName);
    if (!layer) return false;
    const newFrozen = !layer.isFrozen;
    this.setFrozen(layerName, newFrozen);
    return newFrozen;
  }

  /**
   * Set layer locked state (visible but not selectable).
   * @param layerName Layer name
   * @param locked true = locked, false = unlocked
   */
  setLocked(layerName: string, locked: boolean): boolean {
    if (!this.viewer.database) {
      return false;
    }
    const result = setLayerLocked(
      this.viewer.module,
      this.viewer.database,
      layerName,
      locked,
    );
    if (result) {
      this.viewer.update();
    }
    return result;
  }

  /**
   * Toggle layer locked state.
   * @param layerName Layer name
   * @returns New locked state, or false if layer not found
   */
  toggleLocked(layerName: string): boolean {
    const layers = this.getAll();
    const layer = layers.find((l) => l.name === layerName);
    if (!layer) return false;
    const newLocked = !layer.isLocked;
    this.setLocked(layerName, newLocked);
    return newLocked;
  }

  ensureLayerExists(layer: Layer, isUpdateRequired = false) {
    if (!this.viewer.database) return;
    // FIX ME We should refacto color format
    ensureLayerExists(
      this.viewer.module,
      this.viewer.database,
      layer.name,
      layer.color?.toString(),
      layer.lineweight,
      layer.linetype,
    );
    if (isUpdateRequired) {
      updateLayer(
        this.viewer.module,
        this.viewer.database,
        layer.name,
        layer.color?.toString(),
        layer.lineweight,
        layer.linetype,
      );
    }
  }
}

// Re-export LayerInfo for convenience
export type { LayerInfo } from "../database/layers";
